import React from "react";
import { Box } from "@mui/joy";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useTraining } from "../../../../contexts/api";
import type { Module, CourseTemplate } from "../../../../api/followup/Followup.typed";
import type { TrainingTemplate } from "../../../../api/training/Training.typed";
import ModuleForm from "./ModuleForm";
import { SAddModule, SSave, SAddCriteria } from "../../../../styles/StyledMUI";
import { extractDataFromResponse } from "../../../../utils/paginationHelpers";
import { usePermissions, Permission } from "../../../../contexts/app/permissions";
import IconButtonCTA from "../../../common/IconButtonCTA";
import ActionTooltip from "../../../common/ActionTooltip";
import TrainingsEditorDockedActions from "../../common/TrainingsEditorDockedActions";
import TrainingsEmptyState from "../../common/TrainingsEmptyState";
import TrainingsQualificationSelect from "../../common/TrainingsQualificationSelect";
import TrainingsTemplateDashboardFrame from "../../common/TrainingsTemplateDashboardFrame";

export default function CourseTemplateEditor() {
  const [templates, setTemplates] = React.useState<TrainingTemplate[]>();
  const [selectedTemplate, setSelectedTemplate] = React.useState<TrainingTemplate>();
  const [selectedSession, setSelectedSession] = React.useState<CourseTemplate>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const { getTrainingsTemplates, getCourseTemplate, updateCourseTemplate, createCourseTemplate } =
    useTraining();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.TRAININGS_EDIT);

  React.useEffect(() => {
    setIsLoading(true);
    getTrainingsTemplates()
      .then((t) => setTemplates(extractDataFromResponse(t)))
      .finally(() => setIsLoading(false));
  }, [getTrainingsTemplates]);

  const handleChangeTemplate = async (newValue: string | null) => {
    const template = templates?.find((t) => t.ID.toString() === newValue);
    if (template) {
      setSelectedTemplate(template);
      setIsLoading(true);
      try {
        const session = await getCourseTemplate(template.ID);
        setSelectedSession(session);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCreateSession = async () => {
    if (selectedTemplate) {
      const newSession: CourseTemplate = {
        templateId: selectedTemplate.ID,
        version: 1,
        course: { modules: [] },
      };
      setIsLoading(true);
      try {
        await createCourseTemplate(newSession);
        const session = await getCourseTemplate(selectedTemplate.ID);
        setSelectedSession(session);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleModuleChange = (index: number, updatedModule: Module) => {
    if (selectedSession) {
      const updatedModules = [...selectedSession.course.modules];
      updatedModules[index] = updatedModule;
      setSelectedSession({
        ...selectedSession,
        course: {
          ...selectedSession.course,
          modules: updatedModules,
        },
      });
    }
  };

  const handleDeleteModule = (index: number) => {
    if (selectedSession) {
      const updatedModules = [...selectedSession.course.modules];
      updatedModules.splice(index, 1);
      setSelectedSession({
        ...selectedSession,
        course: {
          ...selectedSession.course,
          modules: updatedModules,
        },
      });
    }
  };

  const addModule = () => {
    if (selectedSession) {
      setSelectedSession({
        ...selectedSession,
        course: {
          ...selectedSession.course,
          modules: [...selectedSession.course.modules, { name: "", criteria: [] }],
        },
      });
    }
  };

  const onDragEnd = (result: any) => {
    if (!result.destination || !selectedSession) return;

    const updatedModules = Array.from(selectedSession.course.modules);
    const [movedModule] = updatedModules.splice(result.source.index, 1);
    updatedModules.splice(result.destination.index, 0, movedModule);

    setSelectedSession({
      ...selectedSession,
      course: {
        ...selectedSession.course,
        modules: updatedModules,
      },
    });
  };

  const handleSaveSession = () => {
    if (selectedSession) {
      updateCourseTemplate(selectedSession);
    }
  };

  return (
    <TrainingsTemplateDashboardFrame
      isLoading={isLoading}
      headerLeft={
        <TrainingsQualificationSelect
          templates={templates}
          value={selectedTemplate?.ID.toString() || ""}
          onChange={(newValue) => void handleChangeTemplate(newValue)}
        />
      }
      headerRight={
        selectedTemplate !== undefined && selectedSession === undefined ? (
          <ActionTooltip title="Créer une nouvelle session" disabled={!canEdit}>
            <IconButtonCTA
              ctaVariant="accent"
              aria-label="Créer une nouvelle session"
              onClick={handleCreateSession}
              disabled={!canEdit}
            >
              <SAddCriteria />
            </IconButtonCTA>
          </ActionTooltip>
        ) : null
      }
    >
      {selectedSession ? (
        <Box sx={{ width: "100%" }}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="modules">
              {(provided) => (
                <Box {...provided.droppableProps} ref={provided.innerRef}>
                  {selectedSession.course.modules.length > 0 ? (
                    selectedSession.course.modules.map((module: Module, index: number) => (
                      <Draggable key={index} draggableId={`module-${index}`} index={index}>
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <ModuleForm
                              key={index}
                              module={module}
                              onChange={(updatedModule) => handleModuleChange(index, updatedModule)}
                              onDeleteModule={canEdit ? () => handleDeleteModule(index) : () => {}}
                            />
                          </Box>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <TrainingsEmptyState
                      dense
                      message="Commencez à créer vos modules en cliquant sur le bouton +"
                    />
                  )}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>

          <TrainingsEditorDockedActions>
            <ActionTooltip title="Ajouter module" disabled={!canEdit}>
              <IconButtonCTA
                ctaVariant="accent"
                aria-label="Ajouter module"
                sx={{ marginRight: 2 }}
                onClick={addModule}
                disabled={!canEdit}
              >
                <SAddModule />
              </IconButtonCTA>
            </ActionTooltip>
            <ActionTooltip title="Sauvegarder" disabled={!canEdit}>
              <IconButtonCTA
                ctaVariant="success"
                aria-label="Sauvegarder la session"
                onClick={handleSaveSession}
                disabled={!canEdit}
              >
                <SSave />
              </IconButtonCTA>
            </ActionTooltip>
          </TrainingsEditorDockedActions>
        </Box>
      ) : selectedTemplate !== undefined && selectedSession === undefined ? (
        <TrainingsEmptyState message="Aucun modèle pour cette formation" />
      ) : (
        <TrainingsEmptyState message="Sélectionnez un modèle pour charger la session correspondante." />
      )}
    </TrainingsTemplateDashboardFrame>
  );
}
